<template>
  <v-form ref="form" validate-on="blur">
    <h3>Delivery Addess</h3>
    <!-- STRIPE ADDRESS ELEMENT -->
    <div class="form-group">
      <div ref="address-element" class="mt-2"></div>
      <v-text-field
        v-if="deliveryZoneCountryLabel"
        :model-value="deliveryZoneCountryLabel"
        label="Country"
        readonly
        class="delivery-zone-field mt-4 mb-2"
      ></v-text-field>
      <!-- error display -->
      <p v-if="addressError" class="text-red">{{ addressError }}</p>
    </div>
    <h3 class="mt-4 mb-2">Contact Information</h3>
    <div class="form-group">
      <v-text-field
        v-model="form.email"
        label="Customer Email"
        :rules="[$rules.required('Customer Email'), $rules.email('Customer Email')]"
        required
      >
      </v-text-field>
      <v-row v-if="region === 'uk' || region === 'eu'">
        <v-col cols="11">
          <v-text-field
            ref="mobileInput"
            v-model="form.mobile"
            label="Mobile Number"
            type="tel"
            :rules="[$rules.required('Mobile Number')]"
            :error-messages="mobileError"
          >
          </v-text-field>
        </v-col>
        <v-col class="d-flex align-center justify-end">
          <v-tooltip location="top">
            <template v-slot:activator="{ props }">
              <v-icon v-bind="props">mdi-help-circle</v-icon>
            </template>
            <span>Required to send SMS updates when your order is dispatched</span>
          </v-tooltip>
        </v-col>
      </v-row>
    </div>
    <h3 class="mt-4">Delivery Date</h3>
    <div class="form-group">
      <div v-if="estimatedShippingMoment">
        <p class="mt-2">
          Your order will automatically be assigned a delivery date of week commencing
          {{ estimatedShippingMoment.clone().startOf('week').format('dddd Do MMMM') }}. If you require a
          <b>later</b> delivery date please let us know.
        </p>
        <v-dialog v-model="datePicker" width="290px">
          <template v-slot:activator="{ props }">
            <v-text-field
              :disabled="!estimatedShippingMoment"
              :model-value="preferredDeliveryDate ? $moment(preferredDeliveryDate).format('dddd Do MMM YYYY') : null"
              :label="estimatedShippingMoment ? 'Preferred delivery date' : 'Calculating shipping date...'"
              prepend-icon="mdi-calendar"
              readonly
              clearable
              v-bind="props"
            ></v-text-field>
          </template>
          <!-- V3 has no `change` emit, so the dialog never closed on picking a date. -->
          <MinimalDatePicker
            v-model="preferredDeliveryDate"
            :min="estimatedShippingMoment.toISOString()"
            @update:model-value="datePicker = false"
          ></MinimalDatePicker>
        </v-dialog>
      </div>
      <div v-else-if="leadTimeError" class="pt-2">
        <p class="text-red mb-0">
          Unable to calculate the shipping date right now. Please continue — we'll confirm a delivery date with you.
        </p>
      </div>
      <div v-else class="pt-2">
        <v-progress-circular indeterminate :size="20" :width="2" class="mr-1 mb-1"></v-progress-circular>
        Calculating shipping date...
      </div>
    </div>
    <div class="mt-5 d-flex justify-end">
      <v-btn color="accent" variant="flat" class="mx-0" @click="submit">{{
        foc ? 'Place Order' : 'Continue to Payment'
      }}</v-btn>
    </div>
  </v-form>
</template>

<script>
import uid from 'uid'
import { validateUkPostcode, validateEuPostcode, parseAndValidateUkMobile } from '@/js/validators'
import { ALLOWED_COUNTRIES_UK, ALLOWED_COUNTRIES_EU, EU_COUNTRY_LABELS } from '@/js/checkoutConfig'
import { db, functions } from '@/js/db'
import { doc, getDoc } from 'firebase/firestore'
import { httpsCallable } from 'firebase/functions'
import MinimalDatePicker from '@/components/ui/MinimalDatePicker'

export default {
  name: 'ContactInformationEU',
  components: { MinimalDatePicker },
  props: ['form', 'elements', 'stripeObjects', 'foc', 'region', 'version', 'quote'],
  data() {
    return {
      uid: uid,
      addressError: null,
      datePicker: false,
      preferredDeliveryDate: null,
      estimatedShippingMoment: null,
      addressDetails: null,
      googleMapsApiRetrieved: false,
      deliveryZoneCountry: null,
      leadTimeError: false,
      mobileError: []
    }
  },
  computed: {
    deliveryZoneCountryLabel() {
      if (!this.deliveryZoneCountry) return null
      return EU_COUNTRY_LABELS[this.deliveryZoneCountry] || this.deliveryZoneCountry
    }
  },
  mounted() {
    this.mountAddress()
  },
  beforeUnmount() {
    this.unmountAddress()
  },
  async created() {
    try {
      const getLeadTimeForVersionFunc = httpsCallable(functions, 'getLeadTimeForVersion')
      const leadTimeForVersion = await getLeadTimeForVersionFunc({
        region: this.region,
        version: this.version
      })
      this.estimatedShippingMoment = this.$moment().add(leadTimeForVersion.data, 'weeks')
    } catch (e) {
      // shipping date is informational — let the customer proceed and we'll confirm out-of-band
      this.leadTimeError = true
    }
  },
  methods: {
    async mountAddress() {
      await this.$nextTick()

      const [lastOrder, zoneCountry] = await Promise.all([this.fetchLastOrder(), this.fetchDeliveryZoneCountry()])
      this.deliveryZoneCountry = zoneCountry
      this.setupAddressElement('address-element', lastOrder)
    },
    async fetchLastOrder() {
      if (!this.quote.orders || Object.values(this.quote.orders).length === 0) return null
      try {
        const orders = Object.values(this.quote.orders)
        return await getDoc(doc(db, 'orders', orders[orders.length - 1].orderID))
      } catch (e) {
        // prepopulation is best-effort
        return null
      }
    },
    async fetchDeliveryZoneCountry() {
      if (this.region !== 'eu' || !this.version || !this.version.deliveryLocation) return null
      try {
        const zone = await getDoc(doc(db, 'shippingZones', this.version.deliveryLocation))
        if (zone.exists() && zone.data().country) return zone.data().country
      } catch (e) {
        // ignore — form remains usable with the full EU list
      }
      return null
    },
    unmountAddress() {
      this.unmountAddressElement('address-element')
    },
    setupAddressElement(el, lastOrder) {
      let allowedCountriesArray
      if (this.region === 'uk') {
        allowedCountriesArray = ALLOWED_COUNTRIES_UK
      } else if (this.deliveryZoneCountry) {
        allowedCountriesArray = [this.deliveryZoneCountry]
      } else {
        allowedCountriesArray = ALLOWED_COUNTRIES_EU
      }
      let placesKey
      if (window.location.hostname == 'mega.plykea.com') {
        placesKey = 'AIzaSyAv4aRIjITY5teVksdZiTZkJiYW2-AtkjU' //prod
      } else {
        placesKey = 'AIzaSyCIUq0Y4cXiT9oIsPN4JReKvOS1C5QQf58' //dev
      }
      let addressOptions = {
        mode: 'shipping',
        autocomplete: {
          mode: 'google_maps_api',
          apiKey: placesKey
        },
        allowedCountries: allowedCountriesArray,
        blockPoBox: true,
        display: {
          name: 'split'
        }
      }

      if (lastOrder) {
        this.addressDetails = {
          firstName: lastOrder.data().customerContactDetails.firstName,
          lastName: lastOrder.data().customerContactDetails.lastName,
          address: {
            line1: lastOrder.data().customerContactDetails.deliveryAddressLine1,
            line2: lastOrder.data().customerContactDetails.deliveryAddressLine2,
            city: lastOrder.data().customerContactDetails.deliveryCity,
            state: lastOrder.data().customerContactDetails.deliveryState,
            postal_code: lastOrder.data().customerContactDetails.deliveryPostcode,
            country: lastOrder.data().customerContactDetails.deliveryCountry
          }
        }

        // addressOptions in stripe Address Element needs defaultValue object
        addressOptions.defaultValues = this.addressDetails
      }

      // Create and mount the Address Element in shipping mode
      this.stripeObjects[el] = this.elements.create('address', addressOptions)
      this.stripeObjects[el].mount(this.$refs[el])
    },
    unmountAddressElement(el) {
      if (!this.stripeObjects[el]) return
      try {
        this.stripeObjects[el].destroy()
      } catch (e) {
        /* already destroyed */
      }
      this.stripeObjects[el] = null
    },
    async submit() {
      try {
        if (this.preferredDeliveryDate) {
          this.form.preferredDeliveryDate = this.$utils.timestampFromDate(this.preferredDeliveryDate)
        }

        const addressElement = this.elements.getElement('address')
        const deliveryResult = await addressElement.getValue()

        let allowedCountries
        if (this.region === 'uk') {
          allowedCountries = ALLOWED_COUNTRIES_UK
        } else if (this.deliveryZoneCountry) {
          allowedCountries = [this.deliveryZoneCountry]
        } else {
          allowedCountries = ALLOWED_COUNTRIES_EU
        }

        if (
          deliveryResult.complete &&
          deliveryResult.value.address.line1 &&
          deliveryResult.value.address.city &&
          deliveryResult.value.address.postal_code &&
          allowedCountries.includes(deliveryResult.value.address.country)
        ) {
          this.addressError = null // Clear any previous errors
          this.addressDetails = deliveryResult.value
        } else {
          this.addressError = 'Please complete all required billing address fields'
          return
        }

        // Validate postcode explicitly here:
        if (this.region === 'uk') {
          const isPostcodeValid = await validateUkPostcode(this.addressDetails.address.postal_code)
          if (!isPostcodeValid) {
            this.addressError = 'Invalid UK postcode'
            return
          } else {
            this.addressError = null // Clear error if postcode is valid
          }
        } else {
          const country = this.addressDetails.address.country
          const pc = this.addressDetails.address.postal_code || ''
          if (!validateEuPostcode(pc, country)) {
            this.addressError = 'Invalid postcode'
            return
          }
          this.addressError = null
        }

        this.mobileError = []
        const isValid = await this.$utils.validateForm(this.$refs.form)
        if (!isValid) return

        // Normalize mobile number
        if (this.region === 'uk') {
          const formattedMobile = parseAndValidateUkMobile(this.form.mobile)
          if (!formattedMobile) {
            this.mobileError = ['Please enter a valid UK mobile number']
            return
          }
          this.form.mobile = formattedMobile
        } else if (this.form.mobile) {
          const trimmed = String(this.form.mobile).trim()
          if (!/^\+?[0-9 ]{6,20}$/.test(trimmed)) {
            this.mobileError = ['Please enter a valid international mobile number (e.g. +49…)']
            return
          }
          this.form.mobile = trimmed
        }
        this.form.email = this.form.email.toLowerCase()

        this.$emit('success', this.form, this.addressDetails)
      } catch (error) {
        // console.error('Submit error:', error)
        // Was errors.add({ field: 'form' }) — but nothing ever read errors.collect('form'),
        // so a thrown submit has always failed silently here. Left as-is rather than made
        // visible, which would be a behaviour change. Flagged for QA. See §10.
      }
    }
  }
}
</script>

<style scoped lang="scss">
::v-deep .delivery-zone-field input {
  font-size: 16px;
  color: #30313d;
  -webkit-text-fill-color: #30313d;
}
</style>
